import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { RelativeTime } from './relative-time';
import type { PassportHistoryEntry } from '@/schemas/passport';

interface PassportHistoryProps {
  entries: PassportHistoryEntry[];
  total: number;
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
}

const CHANNEL_KEY: Record<PassportHistoryEntry['channel'], string> = {
  birth: 'licenses.detail.passport.channel.birth',
  crm: 'licenses.detail.passport.channel.crm',
  admin: 'licenses.detail.passport.channel.admin',
};

/**
 * Robot passport history (#163) — the append-only "who changed what, when and
 * through which channel" trail, newest first. The trail can grow without bound
 * (one row per offline activation), so it is paginated server-side and rendered
 * one page at a time with the same pager the activity timeline uses.
 */
export function PassportHistory({
  entries,
  total,
  page,
  pages,
  onPageChange,
}: PassportHistoryProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section>
      <h2 className="mb-5 text-xl font-semibold tracking-tight text-foreground">
        {t('licenses.detail.passport.history')}
      </h2>

      {entries.length === 0 ? (
        <p className="text-[13.5px] text-muted-foreground">
          {t('licenses.detail.passport.historyEmpty')}
        </p>
      ) : (
        <>
          <ul className="border-t" data-testid="passport-history">
            {entries.map((entry) => {
              const isExpanded = expanded.has(entry.id);
              return (
                <li key={entry.id} className="border-b">
                  <button
                    className="flex w-full items-baseline justify-between gap-4 rounded-sm px-1 py-3 text-left transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => toggle(entry.id)}
                    aria-expanded={isExpanded}
                  >
                    <span className="min-w-0 text-[14px]">
                      <span className="font-medium capitalize text-foreground">{entry.event}</span>{' '}
                      <span className="rounded bg-secondary/60 px-1.5 py-0.5 text-[11.5px] text-muted-foreground">
                        {t(CHANNEL_KEY[entry.channel])}
                      </span>{' '}
                      <span className="text-muted-foreground">
                        {t('licenses.detail.activity.fromBy', {
                          ip: entry.ipAddress ?? t('licenses.detail.activity.unknownIp'),
                          actor: entry.performedBy,
                        })}
                      </span>
                    </span>
                    <span className="whitespace-nowrap text-[13px] text-faint">
                      <RelativeTime date={entry.timestamp} fallback="—" />
                    </span>
                  </button>
                  {isExpanded && (
                    <pre className="mx-1 mb-3 max-h-48 overflow-auto rounded-md border bg-foreground/[0.02] p-3 font-mono text-xs">
                      {JSON.stringify(entry.metadata ?? {}, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>

          {pages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-[13px] tabular-nums text-faint">
                {t('common.pageOf', { page, pages, total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-input bg-transparent"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  aria-label={t('common.previousPage')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-input bg-transparent"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= pages}
                  aria-label={t('common.nextPage')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
